<template>
  <div class="log-volume-chart box">
    <!-- Header -->
    <div class="chart-header">
      <span class="chart-title">{{ $t('dailyLogVolume') }}</span>
      <div class="chart-actions">
        <select
          v-model="selectedDays"
          class="days-select"
          :disabled="loading"
          @change="refresh"
        >
          <option v-for="opt in daysOptions" :key="opt" :value="opt">
            {{ opt }} {{ $t('days') || 'ngày' }}
          </option>
        </select>
        <button
          class="chart-refresh-btn"
          :disabled="loading"
          @click="refresh"
        >
          <octicon :icon="syncIcon" :class="{ 'spin': loading }" />
        </button>
      </div>
    </div>

    <!-- Chart -->
    <div class="chart-container" style="height: 300px">
      <chart
        ref="chartRef"
        :type="'line'"
        :series="series"
        :loading="loading"
        :error="error"
        :axis-x-format="'%d/%m/%Y'"
        :tooltip-format="tooltipFormatOptions"
      />
    </div>

    <!-- Footer -->
    <div class="chart-footer">
      <span v-if="lastUpdated" class="footer-item">
        {{ $t('lastUpdated') }}: {{ formatTime(lastUpdated) }}
      </span>
      <span v-if="autoRefresh && !error" class="footer-item">
        {{ $t('autoRefreshIn') }} {{ nextRefreshIn }}s
      </span>
    </div>
  </div>
</template>

<script>
import Chart from '@/components/Chart'
import { sync } from 'octicons-vue'

export default {
  name: 'LogVolumeChart',
  components: { Chart },
  props: {
    refreshInterval: {
      type: Number,
      default: 60000
    },
    autoRefresh: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      chartData: [],
      loading: false,
      error: null,
      lastUpdated: null,
      interval: null,
      nextRefreshIn: 0,
      countdownInterval: null,
      selectedDays: 30,
      daysOptions: [7, 14, 30, 60, 90]
    }
  },
  computed: {
    syncIcon () {
      return sync
    },
    droppedLabel () {
      return this.$t('droppedLogs') || 'Dropped Logs'
    },
    series () {
      if (!this.chartData.length) return { columns: [] }
      const volumeLabel = this.$t('logVolumeLabel')
      return {
        x: 'x',
        columns: [
          ['x', ...this.chartData.map(d => d.date)],
          [volumeLabel, ...this.chartData.map(d => d.bytes)],
          [this.droppedLabel, ...this.chartData.map(d => d.dropped_count || 0)]
        ],
        axes: {
          [this.droppedLabel]: 'y2'
        },
        types: {
          [volumeLabel]: 'bar',
          [this.droppedLabel]: 'bar'
        },
        colors: {
          [this.droppedLabel]: '#ff6b6b'
        }
      }
    },
    tooltipFormatOptions () {
      const self = this
      return {
        title (d) {
          if (d instanceof Date) {
            const dd = String(d.getDate()).padStart(2, '0')
            const mm = String(d.getMonth() + 1).padStart(2, '0')
            const yyyy = d.getFullYear()
            return `${dd}/${mm}/${yyyy}`
          }
          return d
        },
        value (value, ratio, id) {
          if (id === self.droppedLabel) {
            return value.toLocaleString() + ' logs'
          }
          return self.formatBytes(value)
        }
      }
    }
  },
  mounted () {
    this.fetchData()
    if (this.autoRefresh) {
      this.startAutoRefresh()
    }
  },
  beforeDestroy () {
    this.stopAutoRefresh()
  },
  methods: {
    async fetchData () {
      try {
        this.loading = true
        this.error = null
        const response = await this.$http.get('/api/v1/system/log-volume-daily', {
          params: { days: this.selectedDays }
        })

        let data = []
        if (response && response.data && response.data.data && Array.isArray(response.data.data)) {
          data = response.data.data
        } else if (response && response.data && Array.isArray(response.data)) {
          data = response.data
        }

        // Fallback rỗng nếu không có data
        this.chartData = data
        this.lastUpdated = new Date()
      } catch (err) {
        console.error('Failed to fetch log volume:', err)
        this.error = err.message || 'Failed to fetch data'
        this.chartData = []
        this.lastUpdated = new Date()
      } finally {
        this.loading = false
      }
    },

    formatBytes (bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    formatTime (date) {
      if (!date) return ''
      return date.toLocaleTimeString()
    },

    refresh () {
      this.fetchData()
      if (this.autoRefresh) {
        this.resetCountdown()
      }
    },

    startAutoRefresh () {
      this.resetCountdown()
      this.interval = setInterval(() => {
        this.fetchData()
        this.resetCountdown()
      }, this.refreshInterval)
    },

    stopAutoRefresh () {
      if (this.interval) {
        clearInterval(this.interval)
        this.interval = null
      }
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
        this.countdownInterval = null
      }
    },

    resetCountdown () {
      this.nextRefreshIn = Math.floor(this.refreshInterval / 1000)
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
      }
      this.countdownInterval = setInterval(() => {
        this.nextRefreshIn = Math.max(0, this.nextRefreshIn - 1)
      }, 1000)
    }
  }
}
</script>

<style lang="scss" scoped>
.log-volume-chart {
  padding: 1rem;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .is-dark & {
    background: #2c2c2c;
    color: #eee;
  }

  .chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);

    .is-dark & {
      border-bottom-color: rgba(255, 255, 255, 0.1);
    }
  }

  .chart-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .days-select {
    padding: 0.25rem 0.4rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 0.8rem;
    background: transparent;
    color: inherit;
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    .is-dark & {
      border-color: #555;
      background: #2c2c2c;
      color: #eee;
    }
  }

  .chart-title {
    font-weight: 600;
    font-size: 1rem;
  }

  .chart-refresh-btn {
    background: transparent;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 0.25rem 0.5rem;
    cursor: pointer;
    color: inherit;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: rgba(0, 0, 0, 0.05);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    .is-dark & {
      border-color: #555;

      &:hover {
        background: rgba(255, 255, 255, 0.1);
      }
    }
  }

  .chart-container {
    width: 100%;
  }

  .chart-footer {
    display: flex;
    justify-content: space-between;
    margin-top: 1rem;
    padding-top: 0.5rem;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    font-size: 0.75rem;
    color: #888;

    .is-dark & {
      border-top-color: rgba(255, 255, 255, 0.1);
      color: #aaa;
    }

    .footer-item {
      display: inline-block;
    }
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
}
</style>
